import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Megaphone } from "lucide-react";
import { createCampaign } from "../../api/campaigns-api.js";
import {
  AlertDialog,
  AlertDialogCloseButton,
  AlertDialogDescription,
  AlertDialogTitle,
} from "../../components/ui/alert-dialog/index.js";
import { Button } from "../../components/ui/button/index.js";
import { cn } from "../../lib/utils.js";
import {
  contactFormFieldInputClass,
  contactFormFieldLabelClass,
} from "../contacts/contact-sheet-presentation.js";
import {
  contactsModalBodyClass,
  contactsModalFooterClass,
  contactsModalHeaderClass,
} from "../contacts/contacts-dialog-presentation.js";
import { mapCampaignError } from "./campaigns-errors.js";
import { useNotifications } from "../notifications/notification-context.js";
import { campaignListHref } from "./campaigns-presentation.js";
import { useCampaignWorkspace } from "./CampaignWorkspaceLayout.js";
import { CampaignModalContent } from "./CampaignModalContent.js";

export function CampaignCreatePage({
  organizationId,
}: {
  organizationId: number;
}): React.JSX.Element {
  const { t } = useTranslation();
  const { pushNotification } = useNotifications();
  const navigate = useNavigate();
  const { list } = useCampaignWorkspace();
  const [name, setName] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const close = () => {
    navigate(
      campaignListHref({
        status: list.status,
        search: list.search,
        page: list.page,
        pageSize: list.pageSize,
        sort: list.sort,
      }),
    );
  };

  const submit = async () => {
    const trimmed = name.trim();
    if (!trimmed) {
      setError(t("campaigns.create.nameRequired"));
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const response = await createCampaign(organizationId, { name: trimmed });
      list.upsertCreated(response.campaign);
      pushNotification({ message: t("campaigns.toasts.created") });
      navigate(`/campaigns/${response.campaign.id}`, { replace: true });
    } catch (caught) {
      const message = mapCampaignError(t, caught, t("campaigns.create.failed"));
      setError(message);
      pushNotification({ kind: "error", message });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AlertDialog
      open
      onOpenChange={(next) => {
        if (!next && !submitting) close();
      }}
    >
      <CampaignModalContent data-testid="campaign-create-page">
        <div className={contactsModalHeaderClass}>
          <div className="flex items-start gap-2.5 pe-8">
            <span className="inline-grid size-9 shrink-0 place-items-center rounded-xl border border-line/70 bg-surface text-accent">
              <Megaphone size={18} aria-hidden="true" />
            </span>
            <div className="grid min-w-0 gap-1 text-start">
              <AlertDialogTitle>{t("campaigns.create.title")}</AlertDialogTitle>
              <AlertDialogDescription>{t("campaigns.create.subtitle")}</AlertDialogDescription>
            </div>
          </div>
          <AlertDialogCloseButton
            label={t("common.close")}
            onClose={close}
            disabled={submitting}
            className="campaign-create-back"
          />
          <span className="sr-only" data-testid="campaign-create-back">
            {t("campaigns.actions.backToCampaigns")}
          </span>
        </div>
        <form
          className={cn(contactsModalBodyClass, "grid max-w-md gap-4")}
          onSubmit={(event) => {
            event.preventDefault();
            void submit();
          }}
        >
          <label className="grid gap-1.5" htmlFor="campaign-name">
            <span className={contactFormFieldLabelClass}>{t("campaigns.create.nameLabel")}</span>
            <input
              id="campaign-name"
              name="campaignName"
              value={name}
              onChange={(event) => setName(event.target.value)}
              maxLength={200}
              autoFocus
              className={cn(
                contactFormFieldInputClass,
                "duration-[180ms] motion-reduce:duration-0 sm:text-[0.875rem]",
              )}
              placeholder={t("campaigns.create.namePlaceholder")}
              aria-describedby={error ? "campaign-name-error" : undefined}
            />
          </label>
          {error ? (
            <p id="campaign-name-error" className="m-0 text-[0.8125rem] text-error" role="alert">
              {error}
            </p>
          ) : null}
          <div className={contactsModalFooterClass}>
            <Button
              type="submit"
              className="w-full sm:w-auto sm:justify-self-start"
              disabled={submitting}
            >
              {submitting ? t("campaigns.create.submitting") : t("campaigns.create.submit")}
            </Button>
          </div>
        </form>
      </CampaignModalContent>
    </AlertDialog>
  );
}
